import "./bootstrap";
import "maz-ui/styles";
import "../css/app.css";

import { createApp, h, watch } from "vue";
import { createInertiaApp, router, usePage } from "@inertiajs/vue3";
import { createPinia } from "pinia";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { ZiggyVue } from "../../vendor/tightenco/ziggy";
import { i18nVue } from "laravel-vue-i18n";
import PrimeVue from "primevue/config";
import Aura from "@primevue/themes/aura";
import "primeicons/primeicons.css";
import "@fortawesome/fontawesome-free/css/all.css";
import ToastService from "primevue/toastservice";
import VueApexCharts from "vue3-apexcharts";
import Tooltip from "primevue/tooltip";
import BadgeDirective from "primevue/badgedirective";
import { MotionPlugin } from "@vueuse/motion"; // Biblioteca te animações
import ConfirmationService from "primevue/confirmationservice";
import { definePreset } from "@primevue/themes";
import VueTheMask from "vue-the-mask";
import money, { VMoney } from "v-money3";
import { VueReCaptcha } from "vue-recaptcha-v3";
import IuguPlugin, { resolveIuguTestMode } from "./Plugins/iugu";
import { createGtm } from "@gtm-support/vue-gtm";
import { mazUi } from "@maz-ui/themes/presets";
import { MazUi } from "maz-ui/plugins/maz-ui";
import { getPrimeVueLocale } from "@/Services/primeVueUtils";
import { router as InertiaRouter } from "@inertiajs/vue3";
// import * as Sentry from "@sentry/vue";

const appName = import.meta.env.VITE_APP_NAME || "Laravel";
const appEnv = import.meta.env.VITE_APP_ENV || "local";
const lang = import.meta.env.VITE_APP_COUNTRY_CODE == "BR" ? "pt" : "es";
const pinia = createPinia();

const page = usePage();

const MyPreset = definePreset(Aura, {
  semantic: {
    colorScheme: {
      light: {
        primary: {
          color: "{primary}",
        },
        responsive: {
          0: "{slate.950}",
          50: "{slate.900}",
          100: "{slate.800}",
          200: "{slate.700}",
          300: "{slate.600}",
          400: "{slate.500}",
          500: "{slate.400}",
          600: "{slate.300}",
          700: "{slate.200}",
          800: "{slate.100}",
          900: "{slate.50}",
          950: "#ffffff",
        },
      },
      dark: {
        primary: {
          color: "{primary}",
        },
        responsive: {
          0: "#ffffff",
          50: "{slate.50}",
          100: "{slate.100}",
          200: "{slate.200}",
          300: "{slate.300}",
          400: "{slate.400}",
          500: "{slate.500}",
          600: "{slate.600}",
          700: "{slate.700}",
          800: "{slate.800}",
          900: "{slate.900}",
          950: "{slate.950}",
        },
      },
    },
  },
});

createInertiaApp({
  title: (title) => document.querySelector('meta[name="website-page"]') ? title || 'Agendart' : 'Agendart',
  resolve: (name) =>
    resolvePageComponent(
      `./Pages/${name}.vue`,
      import.meta.glob("./Pages/**/*.vue"),
    ),
  setup({ el, App, props, plugin }) {
    const app = createApp({ render: () => h(App, props) })
      .use(plugin)
      .use(MazUi, {
        theme: {
          preset: mazUi,
          colorMode: "light", // sistema só suporta tema claro
        },
      })
      .use(ZiggyVue)
      .use(ToastService)
      .use(VueApexCharts)
      .use(pinia)
      .use(MotionPlugin)
      .use(VueTheMask)
      .use(money, {
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        suffix: "",
        precision: 2,
        masked: false,
      })
      .use(PrimeVue, {
        theme: {
          preset: MyPreset,
          options: {
            darkModeSelector: "", // Deixar essa linha se não quiser aplicar o dark mode com as preferências do windows.
          },
        },
        locale: getPrimeVueLocale(lang),
      })
      .use(i18nVue, {
        lang: lang,
        resolve: (lang) => {
          const langs = import.meta.glob("../../lang/**/*.json", {
            eager: true,
          });
          // Cria um objeto para armazenar todas as traduções
          const translations = {};
          // Itera sobre as chaves dos arquivos importados
          for (const key in langs) {
            // Verifica se o caminho do arquivo corresponde ao padrão desejado
            if (key.startsWith(`../../lang/${lang}/`)) {
              // Mescla as traduções no objeto final
              Object.assign(translations, langs[key].default);
            }
          }

          return translations;
        },
      })
      .use(ConfirmationService);

    // Sentry.init({
    //   app,
    //   dsn: import.meta.env.VITE_SENTRY_DSN_VUE,

    //   sendDefaultPii: true,
    //   integrations: [
    //     Sentry.browserTracingIntegration(),
    //     Sentry.replayIntegration({
    //       maskAllText: false,
    //       blockAllMedia: false,
    //     }),
    //   ],

    //   tracesSampleRate: 1.0, // Capture 100% of the transactions
    //   // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    //   tracePropagationTargets: ["localhost", /^\//],
    //   // Session Replay
    //   replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    //   replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // });

    app.directive("money", VMoney);

    // Add Google Tag Manger
    const hasGoogleTagManager = import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID;
    if (hasGoogleTagManager) {
      app.use(
        createGtm({
          id: import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID,
          debug: import.meta.env.VITE_APP_ENV !== "production", // Set to false in production
        }),
      );
    }

    // Add Recaptcha
    app.use(VueReCaptcha, {
      siteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY, // Your reCAPTCHA site key
      hideBadge: true, // This will hide the badge automatically
    });

    // Adding iugu plugin -> iugu.js
    app.use(IuguPlugin, {
      accountID: import.meta.env.VITE_IUGU_ACCOUNT_ID,
      testMode: resolveIuguTestMode(import.meta.env.VITE_IUGU_TEST_MODE, import.meta.env.VITE_APP_ENV),
    });

    app.directive("tooltip", Tooltip);
    app.directive("badge", BadgeDirective);

    router.on("before", (event) => {
      event.detail.visit.headers["Accept-Language"] = lang;
    });

    // Track Inertia navigation events for GTM
    let gtmNavigationListenerAdded = false;
    if (hasGoogleTagManager && !gtmNavigationListenerAdded) {
      router.on("navigate", (event) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "page_view",
          page: {
            url: event.detail.page.url,
            name: event.detail.page.component,
          },
        });
      });
      gtmNavigationListenerAdded = true;
    }

    // Fionally mount the app
    app.mount(el);
  },

  progress: {
    color: "#3699FF",
  },
});
